import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { components } from '../models/dms-api';

export type SchedulerJob = components['schemas']['SchedulerJob'];
export type SchedulerJobListResponse = components['schemas']['SchedulerJobListResponse'];
export type SchedulerJobRun = components['schemas']['SchedulerJobRun'];
export type SchedulerJobRunPageResponse = components['schemas']['SchedulerJobRunPageResponse'];
export type SchedulerJobRunDetail = components['schemas']['SchedulerJobRunDetail'];
export type SchedulerStatusResponse = components['schemas']['SchedulerStatusResponse'];
export type UpdateSchedulerJobRequest = components['schemas']['UpdateSchedulerJobRequest'];

/** Antwort von POST /jobs/{id}/run (202 Accepted). */
export interface RunDispatchedResponse {
  runDispatched: boolean;
}

/**
 * System-Admin-Client für den DB-gestützten Job-Fahrplan (Scheduler).
 *
 * JSON-Codec (System-Admin-Konvention) — der API-Interceptor erkennt
 * `/api/v1/system/scheduler/jobs…` als JSON-Route (kein msgpack).
 */
@Injectable({ providedIn: 'root' })
export class SystemSchedulerService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = '/system/scheduler/jobs';

  /** Alle Fahrplan-Jobs (alphabetisch nach Schlüssel). */
  list(): Observable<SchedulerJobListResponse> {
    return this.http.get<SchedulerJobListResponse>(this.apiBase);
  }

  /** Detail eines Jobs. */
  get(id: string): Observable<SchedulerJob> {
    return this.http.get<SchedulerJob>(`${this.apiBase}/${id}`);
  }

  /** Konfiguration eines Jobs anpassen (Teilaktualisierung). */
  update(id: string, body: UpdateSchedulerJobRequest): Observable<SchedulerJob> {
    return this.http.patch<SchedulerJob>(`${this.apiBase}/${id}`, body);
  }

  /** Sofort-Lauf anstoßen (Auslöser `manuell`). */
  runNow(id: string): Observable<RunDispatchedResponse> {
    return this.http.post<RunDispatchedResponse>(`${this.apiBase}/${id}/run`, {});
  }

  /** Protokoll der Läufe eines Jobs, jüngste zuerst (paginiert). */
  runs(id: string, page = 1, pageSize = 25): Observable<SchedulerJobRunPageResponse> {
    const params = new HttpParams({
      fromObject: { page: String(page), pageSize: String(pageSize) },
    });
    return this.http.get<SchedulerJobRunPageResponse>(`${this.apiBase}/${id}/runs`, { params });
  }

  /** Einzelner Lauf inkl. voller Prozess-Ausgabe (on-demand beim Aufklappen). */
  runDetail(jobId: string, runId: string): Observable<SchedulerJobRunDetail> {
    return this.http.get<SchedulerJobRunDetail>(`${this.apiBase}/${jobId}/runs/${runId}`);
  }

  /** Aggregierter Problem-Status (Fehlerserien + überfällige Jobs) für das Nav-Badge. */
  status(): Observable<SchedulerStatusResponse> {
    return this.http.get<SchedulerStatusResponse>('/system/scheduler/status');
  }
}
